// UI copy dictionaries (docs/DESIGN.md D9 — browser locale + header toggle;
// scope: UI chrome only, catalog content stays Spanish from the scraper).
// `es` defines the shape; `en` must mirror it (enforced by Messages).
// Rule: every user-facing string lives here — never hardcode copy in components.

export type Locale = "es" | "en";

export const es = {
  localeTag: "es-MX",
  nav: {
    home: "Inicio",
    tours: "Destinos",
    contact: "Contacto",
  },
  hero: {
    kicker: "Más allá del camino estándar",
    title: "Viajes con intención, diseñados a tu medida",
    sub: "Diseño de viajes hecho a mano: circuitos, cruceros y expediciones a los rincones que valen la pena — con todo resuelto.",
    offers: "Ver ofertas vigentes",
    contact: "Habla con un experto →",
  },
  specials: {
    kicker: "Ofertas especiales",
    title: "Ofertas destacadas",
    sub: "Salidas en oferta con cupo limitado — precios por persona en ocupación doble, sujetos a disponibilidad.",
    more: (n: number) => `Ver los ${n} tours del catálogo →`,
  },
  philosophy: {
    kicker: "Nuestra filosofía",
    title: "Más allá del camino estándar",
    sub: "Creemos que un gran viaje no se compra en un catálogo: se diseña. Esto es lo que sostiene cada travesía que armamos.",
    pillars: [
      { title: "Diseño a tu medida", body: "No empaquetamos catálogos: diseñamos cada travesía alrededor de lo que de verdad te importa." },
      { title: "Todo resuelto", body: "Vuelos, hoteles y traslados cuadrados antes de salir. Tu única tarea es hacer la maleta." },
      { title: "Expertos reales", body: "Hablas con personas que conocen los destinos de primera mano — no con un call center." },
    ],
  },
  cta: {
    kicker: "Hablemos",
    title: "¿Listo para tu próxima travesía?",
    body: "Cuéntanos a dónde sueñas ir y te organizamos la propuesta. Sin plantillas, sin prisas.",
    button: "Agenda una consulta",
  },
  catalog: {
    title: "Destinos y planes",
    sub: "Explora los viajes disponibles con nuestros socios. Precios por persona, en moneda indicada.",
    sampleBanner: "Estás viendo datos de muestra. Sincroniza con el portal para mostrar precios y disponibilidad vigentes.",
    warningsBanner: (n: number) => `Última sincronización con advertencias (${n}). Revisa el scraper.`,
    metaLine: (source: string, updated: string) => `Fuente: ${source} · Actualizado: ${updated}`,
    sourceApi: "servidor",
    sourceBundle: "sitio estático",
    searchPlaceholder: "Buscar por destino, hotel o plan…",
    searchAria: "Buscar planes",
    filterAria: "Filtrar por destino",
    allDestinations: "Todos los destinos",
    miscDestination: "Destinos varios",
    emptyState: "No hay planes que coincidan con tu búsqueda.",
  },
  plan: {
    priceOnRequest: "Precio por confirmar",
    datesOnRequest: "Fechas por confirmar",
    perPerson: "por persona",
    roomType: (code: string) => `(habitación ${code})`,
    taxes: (amount: string, currency: string) => `+ ${amount} ${currency} impuestos`,
    taxesShort: "IMP",
    validUntil: (date: string) => `Precios vigentes hasta el ${date}`,
    code: (mt: number) => `Código MT-${mt}`,
    days: (n: number) => `${n} días`,
    nights: (n: number) => `${n} noches`,
    includesFlight: "Incluye vuelo",
  },
  transport: {
    vuelo: "Vuelo incluido",
    autobus: "Autobús",
    crucero: "Crucero",
    terrestre: "Terrestre",
    na: "Traslado por confirmar",
  },
  availability: {
    disponible: "Disponible",
    agotado: "Agotado",
    consultar: "Consultar",
    consultarLong: "Consultar disponibilidad",
  },
  detail: {
    notFoundTitle: "Plan no encontrado",
    notFoundBody: "El plan que buscas ya no está disponible o cambió de nombre.",
    backToCatalog: "Volver a destinos",
    allDestinations: "Todos los destinos",
    dates: "Fechas de salida",
    datesEmpty: "Consulta las salidas disponibles con tu agente.",
    cities: "Ciudades",
    airlines: "Aerolíneas",
    includes: "Incluye",
    notIncludes: "No incluye",
    hotels: "Hoteles previstos",
    rates: "Tarifas por ocupación",
    hotelCol: "Hotel",
    cityCol: "Ciudad",
    typeCol: "Tipo",
    countryCol: "País",
    occupancyCol: "Ocupación",
    yearCol: "Año",
    priceCol: "Precio por persona",
    quote: "Solicitar cotización",
    soldOut: "Este plan está agotado por el momento.",
  },
  // Display-name overlay for the 12 catalog destinations (keyed by id).
  destNames: {
    "europa": "Europa",
    "sudamerica": "Sudamérica",
    "medio-oriente": "Medio Oriente",
    "asia": "Asia",
    "canada": "Canadá",
    "mexico": "México",
    "estados-unidos": "Estados Unidos",
    "cruceros": "Cruceros",
    "centroamerica": "Centroamérica",
    "eventos-especiales": "Eventos especiales",
    "caribe": "Caribe",
    "africa": "África",
  } as Record<string, string>,
  contact: {
    title: "Contacta a tu agente",
    role: "Especialista de Viajes",
    whatsapp: "Contactar por WhatsApp",
    whatsappMessage: "Hola! Vi tu tarjeta digital y estoy interesado en reservar un viaje contigo.",
    altAvatar: "Foto de la agente",
  },
};

export type Messages = typeof es;

export const en: Messages = {
  localeTag: "en-US",
  nav: {
    home: "Home",
    tours: "Tours",
    contact: "Contact",
  },
  hero: {
    kicker: "Beyond the standard path",
    title: "Intentional journeys, curated for you",
    sub: "Hand-crafted travel design: circuits, cruises and expeditions to the corners worth going — with everything sorted.",
    offers: "See current specials",
    contact: "Talk to an expert →",
  },
  specials: {
    kicker: "Hand-picked deals",
    title: "Current specials",
    sub: "Limited-space departure deals — per-person prices in double occupancy, subject to availability.",
    more: (n) => `See all ${n} tours in the catalog →`,
  },
  philosophy: {
    kicker: "Our philosophy",
    title: "Beyond the standard path",
    sub: "We believe a great trip isn't bought off a catalog: it's designed. This is what holds up every journey we build.",
    pillars: [
      { title: "Designed for you", body: "We don't package catalogs: every journey is designed around what truly matters to you." },
      { title: "Everything sorted", body: "Flights, hotels and transfers locked in before departure. Your only job is packing." },
      { title: "Real experts", body: "You talk to people who know the destinations first-hand — not a call center." },
    ],
  },
  cta: {
    kicker: "Speak with us",
    title: "Ready for your next journey?",
    body: "Tell us where you dream of going and we'll put together a proposal. No templates, no rush.",
    button: "Book a consultation",
  },
  catalog: {
    title: "Destinations & tours",
    sub: "Explore the trips available with our partners. Prices per person, in the indicated currency.",
    sampleBanner: "You're viewing sample data. Sync with the portal to show current pricing and availability.",
    warningsBanner: (n) => `Last sync completed with warnings (${n}). Check the scraper.`,
    metaLine: (source, updated) => `Source: ${source} · Updated: ${updated}`,
    sourceApi: "server",
    sourceBundle: "static site",
    searchPlaceholder: "Search by destination, hotel or tour…",
    searchAria: "Search tours",
    filterAria: "Filter by destination",
    allDestinations: "All destinations",
    miscDestination: "Various destinations",
    emptyState: "No tours match your search.",
  },
  plan: {
    priceOnRequest: "Price on request",
    datesOnRequest: "Dates to be confirmed",
    perPerson: "per person",
    roomType: (code) => `(room ${code})`,
    taxes: (amount, currency) => `+ ${amount} ${currency} taxes`,
    taxesShort: "IMP",
    validUntil: (date) => `Prices valid until ${date}`,
    code: (mt) => `Code MT-${mt}`,
    days: (n) => `${n} days`,
    nights: (n) => `${n} nights`,
    includesFlight: "Flight included",
  },
  transport: {
    vuelo: "Flight included",
    autobus: "Bus",
    crucero: "Cruise",
    terrestre: "Land",
    na: "Transfer to be confirmed",
  },
  availability: {
    disponible: "Available",
    agotado: "Sold out",
    consultar: "Ask us",
    consultarLong: "Check availability",
  },
  detail: {
    notFoundTitle: "Tour not found",
    notFoundBody: "The tour you're looking for is no longer available or was renamed.",
    backToCatalog: "Back to destinations",
    allDestinations: "All destinations",
    dates: "Departure dates",
    datesEmpty: "Ask your agent about available departures.",
    cities: "Cities",
    airlines: "Airlines",
    includes: "Included",
    notIncludes: "Not included",
    hotels: "Planned hotels",
    rates: "Rates by occupancy",
    hotelCol: "Hotel",
    cityCol: "City",
    typeCol: "Type",
    countryCol: "Country",
    occupancyCol: "Occupancy",
    yearCol: "Year",
    priceCol: "Price per person",
    quote: "Request a quote",
    soldOut: "This tour is currently sold out.",
  },
  destNames: {
    "europa": "Europe",
    "sudamerica": "South America",
    "medio-oriente": "Middle East",
    "asia": "Asia",
    "canada": "Canada",
    "mexico": "Mexico",
    "estados-unidos": "United States",
    "cruceros": "Cruises",
    "centroamerica": "Central America",
    "eventos-especiales": "Special events",
    "caribe": "Caribbean",
    "africa": "Africa",
  },
  contact: {
    title: "Talk to your agent",
    role: "Travel Specialist",
    whatsapp: "Chat on WhatsApp",
    whatsappMessage: "Hi! I saw your digital card and I'm interested in booking a trip with you.",
    altAvatar: "Agent photo",
  },
};

export const MESSAGES: Record<Locale, Messages> = { es, en };
